<script setup lang="ts">
import { computed } from 'vue'

import type {
    BetaProfileIntegration,
    BetaProfileIntegrationProvider,
} from '@/types/beta-dashboard'

const props = defineProps<{
    provider: BetaProfileIntegrationProvider
    profile?: BetaProfileIntegration | null
    busy?: boolean
}>()

const emit = defineEmits<{
    connect: [provider: BetaProfileIntegrationProvider]
    disconnect: [provider: BetaProfileIntegrationProvider]
}>()

const label = computed(() => props.provider === 'insta' ? 'Instagram' : 'YouTube')

const toggleConnection = () => {
    if (props.profile) emit('disconnect', props.provider)
    else emit('connect', props.provider)
}
</script>

<template>
    <article :class="['beta-profile-integration', `is-${provider}`]">
        <div class="beta-profile-integration__brand">
            <img v-if="profile?.picture" :src="profile.picture" :alt="label">
            <svg v-else-if="provider === 'insta'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" stroke-width="1.8" />
                <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.8" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M21 12c0 2.1-.2 4.3-.7 5.1-.4.7-1 1.2-1.8 1.4-1.5.4-6.5.4-6.5.4s-5 0-6.5-.4c-.8-.2-1.4-.7-1.8-1.4C3.2 16.3 3 14.1 3 12s.2-4.3.7-5.1c.4-.7 1-1.2 1.8-1.4C7 5.1 12 5.1 12 5.1s5 0 6.5.4c.8.2 1.4.7 1.8 1.4.5.8.7 3 .7 5.1Z" stroke="currentColor" stroke-width="1.6" />
                <path d="m10.2 9 4.7 3-4.7 3V9Z" fill="currentColor" />
            </svg>
        </div>

        <div class="beta-profile-integration__content">
            <span>{{ profile ? 'Conectado' : 'Integração de conteúdo' }}</span>
            <strong>{{ profile?.name || label }}</strong>
            <small>{{ profile ? `Perfil conectado ao ${label}` : `Importe conteúdos do ${label}` }}</small>
        </div>

        <button
            type="button"
            :class="profile ? 'is-remove' : 'is-connect'"
            :disabled="busy"
            @click="toggleConnection"
        >
            {{ busy ? 'Aguarde…' : profile ? 'Remover' : 'Conectar' }}
        </button>
    </article>
</template>

<style scoped>
.beta-profile-integration {
    display: grid;
    min-width: 0;
    grid-template-columns: 54px minmax(0, 1fr) auto;
    align-items: center;
    gap: 12px;
    border: 1px solid #e1e7ef;
    border-radius: 16px;
    background: #fff;
    padding: 12px;
}

.beta-profile-integration__brand {
    display: grid;
    width: 54px;
    height: 54px;
    place-items: center;
    overflow: hidden;
    border-radius: 14px;
    color: #fff;
}

.is-insta .beta-profile-integration__brand {
    background: linear-gradient(145deg, #6b4ae8, #df3e74 58%, #f3a23a);
}

.is-youtube .beta-profile-integration__brand {
    background: #e83939;
}

.beta-profile-integration__brand img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.beta-profile-integration__brand svg {
    width: 28px;
    height: 28px;
}

.beta-profile-integration__content {
    min-width: 0;
}

.beta-profile-integration__content span,
.beta-profile-integration__content strong,
.beta-profile-integration__content small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-profile-integration__content span {
    color: #8b99ab;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.beta-profile-integration__content strong {
    margin-top: 3px;
    color: #293f5d;
    font-size: 12px;
    font-weight: 850;
}

.beta-profile-integration__content small {
    margin-top: 3px;
    color: #8492a5;
    font-size: 10px;
}

.beta-profile-integration > button {
    min-height: 32px;
    border-radius: var(--beta-button-radius);
    padding: 0 11px;
    font-size: 9px;
    font-weight: 850;
}

.beta-profile-integration > .is-connect {
    background: var(--main-color);
    color: #fff;
}

.beta-profile-integration > .is-remove {
    background: #f5f7fa;
    color: #a45151;
}

.beta-profile-integration > button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 540px) {
    .beta-profile-integration {
        grid-template-columns: 48px minmax(0, 1fr);
    }

    .beta-profile-integration__brand {
        width: 48px;
        height: 48px;
    }

    .beta-profile-integration > button {
        grid-column: 1 / -1;
    }
}
</style>
